(() => {
  const root = document.querySelector('[data-rd-registration-root]');
  if (!root) return;
  const form = root.querySelector('[data-rd-registration-form]');
  const steps = [...root.querySelectorAll('[data-rd-step]')];
  const progress = [...root.querySelectorAll('[data-rd-progress]')];
  const title = root.querySelector('[data-rd-form-title]');
  const message = root.querySelector('[data-rd-form-message]');
  const mapElement = root.querySelector('[data-rd-map]');
  const lat = form.elements.latitude;
  const lng = form.elements.longitude;
  const coordinate = root.querySelector('[data-rd-coordinate]');
  const contactUrlFields = [...form.querySelectorAll('[data-rd-contact-url]')];
  const mapState = {
    zoom: 13,
    centerLat: Number(lat.value),
    centerLng: Number(lng.value),
    ready: false,
    map: null,
    marker: null,
    initPromise: null,
  };

  const showMessage = (text) => { message.textContent = text; message.hidden = !text; };
  const platformHosts = {
    facebook: ['facebook.com', 'fb.com', 'fb.me'],
    instagram: ['instagram.com'],
    tiktok: ['tiktok.com'],
    shopee: ['shopee.co.th', 'shopee.com'],
  };
  const normalizeContactUrl = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };
  const validateContactUrl = (field) => {
    const value = normalizeContactUrl(field.value);
    if (!value) {
      field.setCustomValidity('');
      return true;
    }
    try {
      const parsed = new URL(value);
      const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
      const allowedHosts = platformHosts[field.dataset.rdContactPlatform];
      const hostMatches = !allowedHosts || allowedHosts.some((allowed) => host === allowed || host.endsWith(`.${allowed}`));
      if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password || !hostMatches) {
        throw new Error('invalid-contact-url');
      }
      parsed.protocol = 'https:';
      parsed.hash = '';
      field.value = parsed.toString();
      field.setCustomValidity('');
      return true;
    } catch (_error) {
      const label = field.closest('label')?.childNodes[0]?.textContent.trim() || 'ช่องทางนี้';
      field.setCustomValidity(`กรุณาวางลิงก์ ${label} ให้ตรงกับช่องครับ`);
      return false;
    }
  };
  const clampLatitude = (value) => Math.max(-85, Math.min(85, Number(value)));
  const wrapLongitude = (value) => ((Number(value) + 540) % 360) - 180;

  const setPin = (latitude, longitude, recenter = false) => {
    lat.value = clampLatitude(latitude).toFixed(6);
    lng.value = wrapLongitude(longitude).toFixed(6);
    coordinate.textContent = `${Number(lat.value).toFixed(5)}, ${Number(lng.value).toFixed(5)}`;
    const position = { lat: Number(lat.value), lng: Number(lng.value) };
    if (mapState.marker) mapState.marker.position = position;
    if (recenter && mapState.map) {
      mapState.centerLat = Number(lat.value);
      mapState.centerLng = Number(lng.value);
      mapState.map.setZoom(mapState.zoom);
      mapState.map.panTo(position);
    }
  };

  const renderMapStatus = (messageText, canRetry = false, isLoading = false) => {
    mapElement.classList.toggle('is-loading', isLoading);
    if (isLoading) mapElement.setAttribute('aria-busy', 'true');
    else mapElement.removeAttribute('aria-busy');
    mapElement.innerHTML = '';
    const status = document.createElement('div');
    status.className = 'rd-map__status';
    status.setAttribute('role', 'status');
    const text = document.createElement('strong');
    text.textContent = messageText;
    status.append(text);
    if (canRetry) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = 'ลองโหลดแผนที่อีกครั้ง';
      retry.addEventListener('click', () => {
        mapState.initPromise = null;
        initMap();
      });
      status.append(retry);
    }
    mapElement.append(status);
  };

  const loadGoogleMaps = (apiKey) => {
    if (window.google?.maps?.importLibrary) return Promise.resolve(window.google.maps);
    if (window.__rdGoogleMapsLoadPromise) return window.__rdGoogleMapsLoadPromise;

    window.__rdGoogleMapsLoadPromise = new Promise((resolve, reject) => {
      const callbackName = '__rdGoogleMapsReady';
      const script = document.createElement('script');
      let timeout;
      const fail = (messageText) => {
        window.clearTimeout(timeout);
        delete window[callbackName];
        script.remove();
        reject(new Error(messageText));
      };
      timeout = window.setTimeout(() => fail('Google Maps ใช้เวลาโหลดนานเกินไป'), 12000);
      window[callbackName] = () => {
        window.clearTimeout(timeout);
        delete window[callbackName];
        resolve(window.google.maps);
      };
      script.async = true;
      script.onerror = () => fail('ไม่สามารถเชื่อมต่อ Google Maps ได้');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&libraries=marker&callback=${callbackName}&language=th&region=TH&auth_referrer_policy=origin&v=weekly`;
      document.head.append(script);
    }).catch((error) => {
      window.__rdGoogleMapsLoadPromise = null;
      throw error;
    });
    return window.__rdGoogleMapsLoadPromise;
  };

  const initMap = () => {
    if (mapState.ready && mapState.map) {
      window.google.maps.event.trigger(mapState.map, 'resize');
      mapState.map.setCenter({ lat: Number(lat.value), lng: Number(lng.value) });
      return Promise.resolve();
    }
    if (mapState.initPromise) return mapState.initPromise;

    const apiKey = root.dataset.googleMapsApiKey?.trim();
    const mapId = root.dataset.googleMapsMapId?.trim();
    if (!apiKey || !mapId) {
      renderMapStatus('กำลังตั้งค่า Google Maps สำหรับร้าน กรุณาลองใหม่ภายหลัง');
      return Promise.resolve();
    }

    renderMapStatus('กำลังโหลด Google Maps…', false, true);
    window.gm_authFailure = () => {
      mapState.ready = false;
      mapState.map = null;
      mapState.marker = null;
      mapState.initPromise = null;
      renderMapStatus('Google Maps ยังยืนยัน API Key ไม่สำเร็จ กรุณาลองใหม่ภายหลัง', true);
    };

    mapState.initPromise = loadGoogleMaps(apiKey).then(async () => {
      const { Map } = await window.google.maps.importLibrary('maps');
      const { AdvancedMarkerElement } = await window.google.maps.importLibrary('marker');
      const position = { lat: Number(lat.value), lng: Number(lng.value) };
      mapElement.innerHTML = '';
      mapState.map = new Map(mapElement, {
        center: position,
        zoom: mapState.zoom,
        mapId,
        clickableIcons: false,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
      });
      mapState.marker = new AdvancedMarkerElement({
        map: mapState.map,
        position,
        title: 'ตำแหน่งร้าน',
        gmpDraggable: true,
      });
      mapState.map.addListener('click', (event) => {
        if (event.latLng) setPin(event.latLng.lat(), event.latLng.lng());
      });
      mapState.marker.addListener('dragend', (event) => {
        if (event.latLng) setPin(event.latLng.lat(), event.latLng.lng());
      });
      mapState.ready = true;
      mapElement.classList.remove('is-loading');
      mapElement.removeAttribute('aria-busy');
    }).catch((error) => {
      mapState.ready = false;
      mapState.map = null;
      mapState.marker = null;
      renderMapStatus(error.message || 'Google Maps โหลดไม่สำเร็จ', true);
    }).finally(() => {
      mapState.initPromise = null;
    });
    return mapState.initPromise;
  };

  const openStep = (step) => {
    steps.forEach((item) => { item.hidden = item.dataset.rdStep !== String(step); });
    progress.forEach((item) => item.classList.toggle('is-active', Number(item.dataset.rdProgress) <= step));
    title.textContent = step === 1 ? 'แนะนำร้านให้เรารู้จัก' : 'ร้านของคุณอยู่ตรงไหน?';
    if (step === 2) window.setTimeout(initMap, 30);
  };

  root.querySelector('[data-rd-next]').addEventListener('click', () => {
    const fields = [...steps[0].querySelectorAll('input:not([type=file]),select')];
    const hasContact = form.elements.phone.value.trim() || form.elements.lineId.value.trim() || contactUrlFields.some((field) => field.value.trim());
    if (!hasContact) { showMessage('กรุณาใส่เบอร์โทร LINE ID หรือลิงก์ช่องทางออนไลน์อย่างน้อย 1 ช่องทางครับ'); return; }
    const contactUrlsValid = contactUrlFields.every(validateContactUrl);
    if (!contactUrlsValid) {
      const invalidField = contactUrlFields.find((field) => !field.validity.valid);
      showMessage(invalidField?.validationMessage || 'กรุณาตรวจลิงก์ช่องทางออนไลน์อีกครั้งครับ');
      invalidField?.reportValidity();
      return;
    }
    if (fields.every((field) => field.reportValidity())) { showMessage(''); openStep(2); }
  });
  contactUrlFields.forEach((field) => {
    field.addEventListener('input', () => { field.setCustomValidity(''); showMessage(''); });
    field.addEventListener('blur', () => validateContactUrl(field));
  });
  root.querySelector('[data-rd-back]').addEventListener('click', () => openStep(1));
  root.querySelector('[data-rd-use-location]').addEventListener('click', () => {
    if (!navigator.geolocation) { showMessage('อุปกรณ์นี้ไม่รองรับตำแหน่ง คุณยังแตะบนแผนที่เพื่อปักหมุดได้'); return; }
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      showMessage('');
      mapState.zoom = 16;
      setPin(coords.latitude, coords.longitude, true);
    }, () => showMessage('ไม่สามารถอ่านตำแหน่งได้ คุณยังแตะบนแผนที่เพื่อปักหมุดได้'), { enableHighAccuracy: true, timeout: 10000 });
  });
  root.querySelector('[data-rd-photo]').addEventListener('change', (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { event.target.value = ''; showMessage('รูปต้องมีขนาดไม่เกิน 5 MB ครับ'); return; }
    root.querySelector('[data-rd-photo-preview]').src = URL.createObjectURL(file);
    root.querySelector('[data-rd-avatar-label]').hidden = true;
    showMessage('');
  });
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!contactUrlFields.every(validateContactUrl)) {
      const invalidField = contactUrlFields.find((field) => !field.validity.valid);
      showMessage(invalidField?.validationMessage || 'กรุณาตรวจลิงก์ช่องทางออนไลน์อีกครั้งครับ');
      invalidField?.reportValidity();
      return;
    }
    if (!form.reportValidity()) return;
    const submit = root.querySelector('[data-rd-submit]');
    submit.disabled = true;
    submit.textContent = 'กำลังส่ง…';
    showMessage('');
    try {
      const response = await fetch(root.dataset.registerEndpoint, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'ส่งข้อมูลไม่สำเร็จ');
      form.hidden = true;
      const success = root.querySelector('[data-rd-success]');
      success.hidden = false;
      root.querySelector('[data-rd-success-message]').textContent = `เลขอ้างอิง ${result.reference} · ทีม RUDEDOG จะตรวจสอบก่อนนำร้านขึ้นเว็บไซต์`;
      success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (error) {
      showMessage(error.message || 'ส่งข้อมูลไม่สำเร็จ กรุณาลองอีกครั้ง');
      submit.disabled = false;
      submit.textContent = 'ส่งลงทะเบียนร้าน';
    }
  });
})();
